/*
 * Cookie banner (custom overlay section)
 *
 * Consent is stored through Shopify's Customer Privacy API, so this banner
 * must only open when the visitor has not made a choice yet.
 *
 * Important: Shopify.customerPrivacy.shouldShowBanner() only reports whether
 * the store collects consent in the visitor's region. It stays true after the
 * visitor accepts or declines, so it has to be combined with the visitor's
 * current consent state. Checking shouldShowBanner() on its own re-opens the
 * banner on every single page load.
 */
class CookieBanner extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing'
    };

    this.banner = this.querySelector('.cookie-banner');
    this.content = this.querySelector('.cookie__content');
    this.preferences = this.querySelector('.cookie__preferences');
    this.consentInputs = this.querySelectorAll('[data-consent-input]');
    this.customizeButton = this.querySelector('button[name="customize"]');
    this.saveButton = this.querySelector('button[name="save"]');
    this.acceptButton = this.querySelector('button[name="accept"]');
    this.declineButton = this.querySelector('button[name="decline"]');

    this.consentCollected = false;
    this.onVisitorConsentCollected = this.onVisitorConsentCollected.bind(this);

    if (this.dataset.testMode === 'true') {
      this.init();
      this.bindButtons();
    } else {
      this.loadCustomerPrivacyApi(0);
    }
  }

  connectedCallback() {
    // Fired by the Customer Privacy API whenever consent is saved, no matter
    // which banner saved it (this one, or Shopify's own cookie banner from
    // Settings > Customer privacy). Close this banner so the visitor is never
    // asked twice.
    document.addEventListener('visitorConsentCollected', this.onVisitorConsentCollected);

    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);

      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('visitorConsentCollected', this.onVisitorConsentCollected);

    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }

  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }

  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }

  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  loadCustomerPrivacyApi(attempt) {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      if (attempt < 20) {
        setTimeout(() => this.loadCustomerPrivacyApi(attempt + 1), 100);
      }
      return;
    }

    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1'
        }
      ],
      (error) => {
        if (!error && window.Shopify.customerPrivacy) {
          this.beforeInit();
        }
      }
    );
  }

  beforeInit() {
    if (!this.shouldOpen()) return;

    this.init();
    this.bindButtons();
  }

  /**
   * Open only when the store collects consent in the visitor's region AND the
   * visitor has not accepted or declined yet. This mirrors the check Shopify's
   * own storefront cookie banner makes.
   */
  shouldOpen() {
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;

    if (!customerPrivacy) return false;
    if (this.consentCollected) return false;
    if (!customerPrivacy.shouldShowBanner()) return false;

    return !this.visitorHasDecided(customerPrivacy);
  }

  visitorHasDecided(customerPrivacy) {
    if (typeof customerPrivacy.currentVisitorConsent === 'function') {
      const consent = customerPrivacy.currentVisitorConsent() || {};
      // Each purpose is 'yes', 'no', or '' (no interaction yet).
      return ['marketing', 'analytics', 'preferences'].some((purpose) => Boolean(consent[purpose]));
    }

    return customerPrivacy.getTrackingConsent() !== 'no_interaction';
  }

  init() {
    if (Shopify && Shopify.designMode) return;

    setTimeout(() => this.open(), parseInt(this.dataset.delay) * 1000);
  }

  open() {
    // Consent may have been given (for example via Shopify's banner) while the
    // opening delay was still running.
    if (this.consentCollected && !Shopify.designMode) return;

    this.banner.classList.add(this.classes.activeClass);
  }

  close() {
    if (!this.banner.classList.contains(this.classes.activeClass)) return;

    this.banner.classList.add(this.classes.closingClass);
    setTimeout(() => {
      this.banner.classList.remove(this.classes.activeClass);
      this.banner.classList.remove(this.classes.closingClass);
    }, 500);
  }

  onVisitorConsentCollected() {
    this.consentCollected = true;
    this.close();
  }

  onButtonClick(event) {
    event.preventDefault();

    switch (event.currentTarget.name) {
      case 'customize':
        this.showPreferences();
        return;
      case 'accept':
        this.setConsent({ analytics: true, marketing: true, preferences: true });
        return;
      case 'decline':
        this.setConsent({ analytics: false, marketing: false, preferences: false });
        return;
      default:
        this.setConsent(this.selectedConsent());
    }
  }

  bindButtons() {
    this.querySelectorAll('.button').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  showPreferences() {
    if (!this.preferences) return;

    this.content.hidden = true;
    this.preferences.hidden = false;
    this.banner.classList.add('has-preferences');

    if (this.customizeButton) this.customizeButton.hidden = true;
    if (this.acceptButton) this.acceptButton.hidden = true;
    if (this.declineButton) this.declineButton.hidden = false;
    if (this.saveButton) this.saveButton.hidden = false;
  }

  selectedConsent() {
    return Array.from(this.consentInputs).reduce(
      (consent, input) => {
        consent[input.name] = input.checked;
        return consent;
      },
      { analytics: false, marketing: false, preferences: false }
    );
  }

  setConsent(consent) {
    this.close();

    if (this.dataset.testMode === 'true') return;
    if (!window.Shopify || !window.Shopify.customerPrivacy) return;

    window.Shopify.customerPrivacy.setTrackingConsent(consent, (result) => {
      if (result && result.error) {
        console.warn('[cookie-banner] Consent could not be saved:', result.error);
      }
    });
  }
}

customElements.get('cookie-banner') || customElements.define('cookie-banner', CookieBanner);
